import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiClient, classifyApiError } from "@/lib/api";

const WINDOW_OPTIONS = [
  { value: "24h", label: "Son 24 saat" },
  { value: "7d", label: "Son 7 gün" },
  { value: "30d", label: "Son 30 gün" },
  { value: "all", label: "Tüm zamanlar" },
];

const ROLE_LABEL = {
  entry_trigger: "Tetikleyici",
  trend_confirmation: "Trend Onay",
  momentum_confirmation: "Momentum Onay",
  sizing_modifier: "Boyutlandırma",
  exit_policy: "Çıkış Politikası",
  scanner_filter: "Tarama Filtresi",
  unknown: "Bilinmeyen",
};

const ROLE_COLOR = {
  entry_trigger: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  trend_confirmation: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  momentum_confirmation: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  sizing_modifier: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  exit_policy: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  scanner_filter: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  unknown: "bg-gray-500/15 text-gray-300 border-gray-500/40",
};

const fmtNumber = (n) => {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
};

const fmtPct = (n) => {
  if (n === null || n === undefined) return "—";
  return `%${Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`;
};

const fmtUsd = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} USDT`;
};

const DirCol = ({ label, data, accent }) => {
  const total = Number(data?.total_opened || 0);
  const profit = Number(data?.profit_count || 0);
  const loss = Number(data?.loss_count || 0);
  const open = Number(data?.open_count || 0);
  const breakeven = Number(data?.breakeven_count || 0);
  const wr = data?.win_rate_pct;
  const pnl = Number(data?.net_pnl || 0);
  return (
    <div
      data-testid={`strategy-card-dir-${accent}`}
      className={`flex-1 rounded-lg border px-3 py-2 ${
        accent === "long"
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-rose-500/40 bg-rose-500/10"
      }`}
    >
      <div className="mb-1 flex items-center justify-between">
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            accent === "long" ? "text-emerald-300" : "text-rose-300"
          }`}
        >
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{fmtNumber(total)} açılış</span>
      </div>
      <div className="grid grid-cols-4 gap-1 text-xs">
        <div>
          <div className="text-muted-foreground">Kâr</div>
          <div className="font-semibold text-emerald-400">{fmtNumber(profit)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Zarar</div>
          <div className="font-semibold text-rose-400">{fmtNumber(loss)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Açık</div>
          <div className="font-semibold text-foreground">{fmtNumber(open)}</div>
        </div>
        <div data-testid={`strategy-card-breakeven-${accent}`}>
          <div className="text-muted-foreground">Başabaş</div>
          <div className="font-semibold text-amber-400">{fmtNumber(breakeven)}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">WR: <strong className="text-foreground">{fmtPct(wr)}</strong></span>
        <span className={`font-mono font-semibold ${pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
          {fmtUsd(pnl)}
        </span>
      </div>
    </div>
  );
};

const StrategyCard = ({ item, onUseInBot }) => {
  const roleKey = (item.role || "unknown").toLowerCase();
  const totalOpened = Number(item.combined?.total_opened || 0);
  const lastOpened = item.last_opened_at ? new Date(item.last_opened_at).toLocaleString("tr-TR") : "Hiç açılmadı";
  return (
    <Card data-testid={`strategy-card-${item.strategy_code}`} className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate" title={item.title}>
              {item.title}
            </CardTitle>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={`text-[10px] ${ROLE_COLOR[roleKey] || ROLE_COLOR.unknown}`}>
                {ROLE_LABEL[roleKey] || roleKey}
              </Badge>
              {item.timeframe && (
                <Badge variant="outline" className="text-[10px]">
                  {item.timeframe}
                </Badge>
              )}
              <Badge
                data-testid={`strategy-l3-${item.strategy_code}`}
                variant="outline"
                className={`text-[10px] ${
                  item.l3_active
                    ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                    : "border-rose-500/50 bg-rose-500/15 text-rose-300"
                }`}
              >
                {item.l3_active ? "L3 Aktif" : "L3 Kapalı"}
              </Badge>
              {!item.registered && (
                <Badge variant="outline" className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/50">
                  Eski/Bilinmeyen
                </Badge>
              )}
            </div>
          </div>
          <div className="text-right">
            <div
              data-testid={`strategy-card-total-${item.strategy_code}`}
              className="font-mono text-2xl font-bold text-foreground"
            >
              {fmtNumber(totalOpened)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Toplam Açılış</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <div className="flex gap-2">
          <DirCol label="LONG" data={item.long} accent="long" />
          <DirCol label="SHORT" data={item.short} accent="short" />
        </div>
        <div className="flex items-center justify-between border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">Son: {lastOpened}</span>
          <span
            data-testid={`strategy-card-net-pnl-${item.strategy_code}`}
            className={`font-mono text-sm font-bold ${
              Number(item.combined?.net_pnl || 0) >= 0 ? "text-emerald-400" : "text-rose-400"
            }`}
          >
            {fmtUsd(item.combined?.net_pnl || 0)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            data-testid={`strategy-card-use-long-${item.strategy_code}`}
            disabled={!item.registered}
            onClick={() => onUseInBot(item.strategy_code, "long")}
          >
            LONG Botu Oluştur
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            data-testid={`strategy-card-use-short-${item.strategy_code}`}
            disabled={!item.registered}
            onClick={() => onUseInBot(item.strategy_code, "short")}
          >
            SHORT Botu Oluştur
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SummaryBar = ({ data }) => {
  const items = data?.items || [];
  const totalOpened = items.reduce((s, i) => s + Number(i.combined?.total_opened || 0), 0);
  const totalPnl = items.reduce((s, i) => s + Number(i.combined?.net_pnl || 0), 0);
  const totalLong = items.reduce((s, i) => s + Number(i.long?.total_opened || 0), 0);
  const totalShort = items.reduce((s, i) => s + Number(i.short?.total_opened || 0), 0);
  const activeCount = data?.active_count || 0;
  const registryCount = data?.registry_count || 0;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="strategy-perf-summary">
      <Card>
        <CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">L3 Aktif Strateji</div>
          <div className="font-mono text-2xl font-bold text-foreground">
            {activeCount} / {registryCount}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Toplam Açılış</div>
          <div className="font-mono text-2xl font-bold text-foreground">{fmtNumber(totalOpened)}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300">LONG Açılış</div>
          <div className="font-mono text-2xl font-bold text-emerald-400">{fmtNumber(totalLong)}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-rose-300">SHORT Açılış</div>
          <div className="font-mono text-2xl font-bold text-rose-400">{fmtNumber(totalShort)}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net PnL</div>
          <div className={`font-mono text-2xl font-bold ${totalPnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {fmtUsd(totalPnl)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function UserStrategyPerformancePage() {
  const navigate = useNavigate();
  const [windowSel, setWindowSel] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | with_trades | empty | long_only | short_only

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const res = await apiClient.get("/user/strategies/performance", {
        params: { window: w },
        timeout: 15000,
      });
      setData(res.data);
    } catch (err) {
      toast.error(classifyApiError(err)?.message || "Strateji performansı yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/user/strategies/performance", {
          params: { window: windowSel },
          timeout: 15000,
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(classifyApiError(err)?.message || "Strateji performansı yüklenemedi");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [windowSel]);

  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    if (filter === "all") return items;
    if (filter === "with_trades") {
      return items.filter((i) => Number(i.combined?.total_opened || 0) > 0);
    }
    if (filter === "empty") {
      return items.filter((i) => Number(i.combined?.total_opened || 0) === 0);
    }
    if (filter === "long_only") {
      return items.filter((i) => Number(i.long?.total_opened || 0) > Number(i.short?.total_opened || 0));
    }
    if (filter === "short_only") {
      return items.filter((i) => Number(i.short?.total_opened || 0) > Number(i.long?.total_opened || 0));
    }
    return items;
  }, [data, filter]);

  const handleUseInBot = (strategyCode, direction) => {
    // Bot wizard'a strategy_code + scope prefilled ile yönlendir
    const scope = direction === "long" ? "long_only" : "short_only";
    navigate(
      `/user/strategies?strategy_code=${encodeURIComponent(strategyCode)}&direction_scope=${scope}`,
      { replace: false }
    );
  };

  return (
    <div className="space-y-4 p-4" data-strategy-perf-bypass="true" data-testid="user-strategy-performance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Strateji Performansı</h1>
          <p className="text-sm text-muted-foreground">
            Her stratejinin LONG / SHORT açılış sayısı, kâr/zarar dağılımı ve net PnL&apos;i.
            Kötü performans gösteren stratejiyi botunuzdan çıkarın, iyi olanı yön bazlı seçin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={windowSel} onValueChange={setWindowSel}>
            <TabsList data-testid="strategy-perf-window-tabs">
              {WINDOW_OPTIONS.map((opt) => (
                <TabsTrigger key={opt.value} value={opt.value} data-testid={`strategy-perf-window-${opt.value}`}>
                  {opt.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => load(windowSel)}
            disabled={loading}
            data-testid="strategy-perf-refresh-btn"
          >
            {loading ? "Yükleniyor..." : "Yenile"}
          </Button>
        </div>
      </div>

      <SummaryBar data={data} />

      <div className="flex flex-wrap items-center gap-2">
        {[
          { v: "all", l: "Tümü" },
          { v: "with_trades", l: "İşlemi Olanlar" },
          { v: "empty", l: "İşlemi Olmayanlar" },
          { v: "long_only", l: "LONG Ağırlıklı" },
          { v: "short_only", l: "SHORT Ağırlıklı" },
        ].map((f) => (
          <Button
            key={f.v}
            type="button"
            variant={filter === f.v ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.v)}
            data-testid={`strategy-perf-filter-${f.v}`}
          >
            {f.l}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredItems.length} / {data?.items?.length || 0} strateji gösteriliyor
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="strategy-perf-grid">
        {filteredItems.map((item) => (
          <StrategyCard key={item.strategy_code} item={item} onUseInBot={handleUseInBot} />
        ))}
        {filteredItems.length === 0 && !loading && (
          <div className="col-span-full rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Bu filtreyle eşleşen strateji yok.
          </div>
        )}
      </div>
    </div>
  );
}
